import {
  CheckCircle as CheckIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  useBackgroundSync,
  useLatestSyncMutation,
  useSyncMetadata,
  useSyncsRunningMutationsCount,
} from "../hooks/data-sync.hooks";
import { formatRelativeTime } from "../libs/utils";
import { ErrorGuard } from "./ErrorGuard";
import { LoadingGuard } from "./LoadingGuard";

/**
 * Componente per mostrare lo stato di sincronizzazione nell'header
 * Mostra un'icona con tooltip e al click apre un popover con dettagli
 */
export const SyncMetadataWidget = () => {
  const runningSyncsCount = useSyncsRunningMutationsCount();
  const lastSyncRun = useLatestSyncMutation();
  const { data: syncMetadata, isLoading, error } = useSyncMetadata();
  const backgroundSync = useBackgroundSync();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (isLoading || error || !syncMetadata) {
    return (
      <Tooltip title="Caricamento metadati di sincronizzazione...">
        <IconButton size="small" disabled>
          <SyncIcon />
        </IconButton>
      </Tooltip>
    );
  }

  const getLastSyncTime = (): number => {
    return Math.max(
      syncMetadata.matches.lastSyncedAt || 0,
      syncMetadata.match_events.lastSyncedAt || 0
    );
  };

  const lastSyncTime = getLastSyncTime();
  const formattedTime = formatRelativeTime(lastSyncTime);
  const isRecentSync =
    lastSyncTime > 0 && Date.now() - lastSyncTime < 5 * 60 * 1000; // ultimi 5 minuti

  return (
    <LoadingGuard
      isLoading={runningSyncsCount > 0}
      loadingComponent={
        <Stack
          direction={"row"}
          spacing={1}
          alignItems="center"
          width={"100%"}
          height={"100%"}
        >
          {/* <CircularProgress size={24} />{" "} */}
          <small style={{ lineHeight: 1, opacity: 0.7 }}>
            Sincronizzazione in corso..
          </small>
        </Stack>
      }
    >
      <Tooltip title={`Ultimo sync: ${formattedTime}`}>
        <IconButton
          size="small"
          onClick={handleClick}
          color={isRecentSync ? "success" : "default"}
        >
          <ErrorGuard error={error || lastSyncRun?.error}>
            <LoadingGuard
              isLoading={runningSyncsCount > 0}
              loadingComponent={
                <small style={{ lineHeight: 1, opacity: 0.7 }}>
                  Sincronizzazione in corso..
                </small>
              }
            >
              {isRecentSync ? <CheckIcon /> : <SyncIcon />}
            </LoadingGuard>
          </ErrorGuard>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Card>
          <CardHeader title="Stato Sincronizzazione" />
          <CardContent>
            <Stack spacing={2}>
              {/* Matches */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Matches
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    {formatRelativeTime(syncMetadata.matches.lastSyncedAt || 0)}
                  </Typography>
                  {/* <Chip
                  size="small"
                  label={
                    syncMetadata.matches.lastItemId
                      ? "Sincronizzato"
                      : "Non sincronizzato"
                  }
                  color={
                    syncMetadata.matches.lastItemId ? "success" : "warning"
                  }
                  variant="outlined"
                /> */}
                </Box>
                {syncMetadata.matches.lastItemId && (
                  <Typography variant="caption" color="text.secondary">
                    ID: {syncMetadata.matches.lastItemId.substring(0, 8)}...
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Match Events */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Eventi
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    {formatRelativeTime(
                      syncMetadata.match_events.lastSyncedAt || 0
                    )}
                  </Typography>
                  {/* <Chip
                  size="small"
                  label={
                    syncMetadata.match_events.lastItemId
                      ? "Sincronizzato"
                      : "Non sincronizzato"
                  }
                  color={
                    syncMetadata.match_events.lastItemId ? "success" : "warning"
                  }
                  variant="outlined"
                /> */}
                </Box>
                {syncMetadata.match_events.lastItemId && (
                  <Typography variant="caption" color="text.secondary">
                    ID: {syncMetadata.match_events.lastItemId.substring(0, 8)}
                    ...
                  </Typography>
                )}
              </Box>

              {/* Metadata globali */}
              {(syncMetadata.matches.skips ||
                syncMetadata.match_events.skips) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Statistiche Sync
                    </Typography>
                    {syncMetadata.matches.skips && (
                      <Typography variant="caption" display="block">
                        Matches saltati: {syncMetadata.matches.skips}
                      </Typography>
                    )}
                    {syncMetadata.match_events.skips && (
                      <Typography variant="caption" display="block">
                        Eventi saltati: {syncMetadata.match_events.skips}
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
          <CardActions>
            <Button onClick={() => backgroundSync.mutate()}>
              Avvia Sincronizzazione
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </LoadingGuard>
  );
};
